import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ProspectionPRCarteView from './ProspectionPRCarteView';
import './ProspectionPR.css';

const EMPTY_FORM = {
  nom: '', concession_id: '', code_client_datacar: '',
  adresse: '', latitude: '', longitude: '', date_derniere_visite: '',
  contact_nom: '', contact_prenom: '', contact_telephone: '', contact_email: '',
};

const MOIS_ABBR = ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'];
function formatMoisLabel(key) {
  const [y, m] = key.split('-');
  return `${MOIS_ABBR[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}
function formatMontant(v) {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

function ProspectionPR({ user, editMode, setHeaderAction }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [activeTab, setActiveTab] = useState('clients'); // 'clients' | 'carte' | 'reporting'
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [concessions, setConcessions] = useState([]);
  const [selected, setSelected] = useState(new Set());

  const [modalClient, setModalClient] = useState(null); // null fermé, {} nouveau, {...} édition
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reporting — CA comptoir Pièces (M1/M2) par client, mois de l'année en cours
  const [caData, setCaData] = useState({ months: [], clients: [] });
  const [caLoading, setCaLoading] = useState(false);

  useEffect(() => {
    api.get('/concessions').then(r => setConcessions(r.data.concessions || r.data || [])).catch(() => {});
  }, []);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterConcession) params.concession_id = filterConcession;
      if (search) params.search = search;
      const res = await api.get('/prospection-pr', { params });
      setClients(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterConcession, search]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadCa = useCallback(async () => {
    setCaLoading(true);
    try {
      const params = {};
      if (filterConcession) params.concession_id = filterConcession;
      const res = await api.get('/prospection-pr/reporting/ca-mensuel', { params });
      setCaData(res.data || { months: [], clients: [] });
    } catch (e) { console.error(e); }
    setCaLoading(false);
  }, [filterConcession]);

  useEffect(() => { if (activeTab === 'reporting') loadCa(); }, [activeTab, loadCa]);

  // Bouton d'en-tête — uniquement sur l'onglet Clients
  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'clients') {
      setHeaderAction({ primary: { label: '+ Nouveau client', onClick: () => openModal({}) } });
    } else {
      setHeaderAction(null);
    }
  }, [setHeaderAction, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (client) => {
    setModalClient(client);
    setForm(client && client.id ? {
      nom: client.nom || '',
      concession_id: client.concession_id || '',
      code_client_datacar: client.code_client_datacar || '',
      adresse: client.adresse || '',
      latitude: client.latitude ?? '',
      longitude: client.longitude ?? '',
      date_derniere_visite: client.date_derniere_visite ? String(client.date_derniere_visite).split('T')[0] : '',
      contact_nom: client.contact_nom || '',
      contact_prenom: client.contact_prenom || '',
      contact_telephone: client.contact_telephone || '',
      contact_email: client.contact_email || '',
    } : EMPTY_FORM);
    setError('');
  };

  const closeModal = () => { setModalClient(null); setForm(EMPTY_FORM); setError(''); };

  const handleSave = async () => {
    if (!form.nom.trim()) { setError('Le nom est requis.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        concession_id: form.concession_id || null,
        latitude: form.latitude === '' ? null : parseFloat(form.latitude),
        longitude: form.longitude === '' ? null : parseFloat(form.longitude),
        date_derniere_visite: form.date_derniere_visite || null,
      };
      if (modalClient && modalClient.id) {
        await api.put(`/prospection-pr/${modalClient.id}`, payload);
      } else {
        await api.post('/prospection-pr', payload);
      }
      closeModal();
      loadClients();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelection = async () => {
    if (!window.confirm(`Supprimer ${selected.size} client(s) sélectionné(s) ?`)) return;
    try {
      await Promise.all([...selected].map(id => api.delete(`/prospection-pr/${id}`)));
      setSelected(new Set());
      loadClients();
    } catch (e) {
      alert('Erreur lors de la suppression.');
    }
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="page-container">

      {/* Onglets — toujours en premier */}
      <div className="page-tabs">
        <button className={activeTab === 'clients' ? 'active' : ''} onClick={() => setActiveTab('clients')}>👥 Clients</button>
        <button className={activeTab === 'carte' ? 'active' : ''} onClick={() => setActiveTab('carte')}>🗺️ Carte</button>
        <button className={activeTab === 'reporting' ? 'active' : ''} onClick={() => setActiveTab('reporting')}>📊 Reporting</button>
      </div>

      {/* Filtres */}
      <div className="page-toolbar">
        <input
          type="search"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {(isSuperAdmin || user?.role === 'teleoperateur') && concessions.length > 1 && (
          <select className={`page-select${filterConcession ? ' active-filter' : ''}`} value={filterConcession} onChange={e => setFilterConcession(e.target.value)}>
            <option value="">Toutes concessions</option>
            {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}
      </div>

      {activeTab === 'clients' && (
        <>
          {editMode && isSuperAdmin && selected.size > 0 && (
            <div className="ppr-bulk-bar">
              <strong>{selected.size}</strong> sélectionné(s)
              <button className="ppr-btn-delete-selection" onClick={handleDeleteSelection}>🗑️ Supprimer la sélection</button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
          ) : clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              Aucun client. Cliquez sur « + Nouveau client » pour commencer.
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {editMode && isSuperAdmin && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={selected.size === clients.length && clients.length > 0}
                          onChange={e => setSelected(e.target.checked ? new Set(clients.map(c => c.id)) : new Set())}
                        />
                      </th>
                    )}
                    <th>Nom</th>
                    <th>Concession</th>
                    <th>Code client Datacar</th>
                    <th>Adresse</th>
                    <th>Contact</th>
                    <th style={{ width: 110, textAlign: 'center' }}>Géolocalisé</th>
                    <th style={{ width: 130 }}>Dernière visite</th>
                    <th style={{ width: 60 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => {
                    const geoloc = c.latitude != null && c.longitude != null;
                    return (
                      <tr key={c.id} onClick={() => openModal(c)} style={{ cursor: 'pointer' }}>
                        {editMode && isSuperAdmin && (
                          <td onClick={e => e.stopPropagation()}>
                            <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelected(c.id)} />
                          </td>
                        )}
                        <td style={{ fontWeight: 600 }}>{c.nom}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.concession_nom || '—'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.code_client_datacar || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.adresse || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {(c.contact_prenom || c.contact_nom) ? (
                            <span title={[c.contact_telephone, c.contact_email].filter(Boolean).join(' · ')}>
                              {[c.contact_prenom, c.contact_nom].filter(Boolean).join(' ')}
                            </span>
                          ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>{geoloc ? '✅' : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ fontSize: 13 }}>{c.date_derniere_visite ? new Date(c.date_derniere_visite).toLocaleDateString('fr-FR') : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="action-buttons">
                            <button className="btn-icon" title="Modifier" onClick={() => openModal(c)}>✏️</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'carte' && (
        <ProspectionPRCarteView clients={clients} onOpenClient={openModal} />
      )}

      {activeTab === 'reporting' && (
        caLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
        ) : caData.clients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            Aucun client à afficher.
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
              CA HT comptoir Pièces (facturation « Magasin/Boutique »), mois de l'année en cours — rapprochement par nom client avec Factures Services.
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    {concessions.length > 1 && <th>Concession</th>}
                    {caData.months.map(m => (
                      <th key={m} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoisLabel(m)}</th>
                    ))}
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {caData.clients.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.nom}</td>
                      {concessions.length > 1 && <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{c.concession_nom || '—'}</td>}
                      {caData.months.map(m => (
                        <td key={m} style={{ textAlign: 'right', fontSize: 13, color: c.montants[m] ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {c.montants[m] ? formatMontant(c.montants[m]) : '—'}
                        </td>
                      ))}
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMontant(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, background: 'var(--bg-muted)' }}>
                    <td>Total comptoir</td>
                    {concessions.length > 1 && <td></td>}
                    {caData.months.map(m => (
                      <td key={m} style={{ textAlign: 'right' }}>
                        {formatMontant(caData.clients.reduce((s, c) => s + (c.montants[m] || 0), 0))}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right' }}>
                      {formatMontant(caData.clients.reduce((s, c) => s + c.total, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )
      )}

      {/* Modale création/modification */}
      {modalClient && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalClient.id ? 'Modifier le client' : 'Nouveau client'}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Nom *</label>
                  <input type="text" placeholder="Nom du garage / client" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Concession</label>
                  <select value={form.concession_id} onChange={e => setForm(f => ({ ...f, concession_id: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }}>
                    <option value="">— Sélectionner —</option>
                    {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Code client Datacar</label>
                  <input type="text" value={form.code_client_datacar} onChange={e => setForm(f => ({ ...f, code_client_datacar: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Adresse</label>
                  <textarea rows={2} value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Latitude</label>
                    <input type="number" step="0.0000001" placeholder="Ex: 14.6415" value={form.latitude} onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Longitude</label>
                    <input type="number" step="0.0000001" placeholder="Ex: -61.0242" value={form.longitude} onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Date dernière visite</label>
                  <input type="date" value={form.date_derniere_visite} onChange={e => setForm(f => ({ ...f, date_derniere_visite: e.target.value }))} style={{ padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>

                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4 }}>
                  Contact principal
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Prénom</label>
                    <input type="text" value={form.contact_prenom} onChange={e => setForm(f => ({ ...f, contact_prenom: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Nom</label>
                    <input type="text" value={form.contact_nom} onChange={e => setForm(f => ({ ...f, contact_nom: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Numéro de téléphone</label>
                    <input type="tel" value={form.contact_telephone} onChange={e => setForm(f => ({ ...f, contact_telephone: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Adresse e-mail</label>
                    <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                  </div>
                </div>
                {error && (
                  <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                    {error}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Annuler</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving || !form.nom.trim()}>
                {saving ? 'Enregistrement...' : (modalClient.id ? 'Enregistrer' : 'Créer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProspectionPR;
